import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Send, MessageSquare } from 'lucide-react';

export interface IntakeSendSmsReviewModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSend: (message: string) => Promise<void>;
  customerPhone: string;
  customerFirstName: string;
  businessName: string;
  intakeUrl: string;
  /** Shown in header, e.g. form title */
  formTitle?: string;
}

export default function IntakeSendSmsReviewModal({
  isOpen,
  onDismiss,
  onSend,
  customerPhone,
  customerFirstName,
  businessName,
  intakeUrl,
  formTitle,
}: IntakeSendSmsReviewModalProps) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const first = (customerFirstName || 'there').trim() || 'there';
  const biz = (businessName || 'Our team').trim() || 'Our team';

  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setSending(false);
      return;
    }
    setMessage(`Hi ${first}, please complete this short form from ${biz}:\n\n${intakeUrl}\n\nThank you!`);
  }, [isOpen, first, biz, intakeUrl]);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await onSend(message.trim());
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[20030] flex items-end sm:items-center justify-center sm:p-4">
      <button type="button" className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !sending && onDismiss()} aria-label="Close" />
      <div
        className="relative w-full sm:max-w-lg bg-white sm:rounded-2xl shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900">Review SMS</h2>
              <p className="text-xs text-gray-500 truncate">{formTitle || 'Intake form link'}</p>
            </div>
          </div>
          <button
            type="button"
            disabled={sending}
            onClick={onDismiss}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800">{customerPhone}</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
            <textarea
              rows={9}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending}
              className="w-full px-3 py-2.5 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-500 resize-none disabled:opacity-50"
              style={{ fontSize: '16px', WebkitTapHighlightColor: 'transparent' }}
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Edit for this send only. The form link is added automatically if you remove it.
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{message.length} characters</p>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex gap-2" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
          <button
            type="button"
            disabled={sending}
            onClick={onDismiss}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={sending || !message.trim()}
            onClick={() => void handleSend()}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send SMS
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
